import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';

import { BrowserLink } from '../../atoms/BrowserLink/BrowserLink';
import { SkeletonBlock } from '../../atoms/SkeletonBlock';
import { InformationIcon } from '../../icons';
import LeftChevronIcon from '../../icons/ChevronLeft.svg';

export interface MplTopSectionHeaderProps {
	title: string;
	subtitle?: string;
	onBack?: VoidFunction;
	infoUrl?: string;
	dataTestId?: string;
	isTitleLoading?: boolean;
}

export function MplTopSectionHeader({
	title,
	subtitle,
	onBack,
	infoUrl,
	dataTestId,
	isTitleLoading,
}: MplTopSectionHeaderProps) {
	const theme = useTheme();

	return (
		<Stack direction="column" width="100%" color="primary.main" gap={1}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				width="100%"
				sx={{ position: 'relative' }}
			>
				<Typography
					variant="h3-sb"
					color="primary.main"
					sx={{
						flex: 1,
						textAlign: 'center',
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						px: '48px',
						minHeight: 24,
						// Flex display only while showing skeleton — flex breaks
						// text-overflow: ellipsis on text content.
						...(isTitleLoading && {
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}),
					}}
				>
					{isTitleLoading ? <SkeletonBlock w={140} h={20} r={6} /> : title}
				</Typography>

				<Box
					sx={{
						position: 'absolute',
						left: 0,
						top: '50%',
						transform: 'translateY(-50%)',
					}}
				>
					{onBack && (
						<IconButton
							onClick={onBack}
							data-testid={`${dataTestId}-back-button`}
						>
							<LeftChevronIcon />
						</IconButton>
					)}
				</Box>

				<Box
					sx={{
						position: 'absolute',
						right: 0,
						top: '50%',
						transform: 'translateY(-50%)',
					}}
				>
					{infoUrl && (
						<BrowserLink url={infoUrl}>
							<IconButton data-testid={`${dataTestId}-info-button`}>
								<InformationIcon />
							</IconButton>
						</BrowserLink>
					)}
				</Box>
			</Stack>

			{subtitle && (
				<Typography
					variant="caption"
					color={theme.palette.solutions?.[600]}
					sx={{
						width: '100%',
						textAlign: 'center',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{subtitle}
				</Typography>
			)}
		</Stack>
	);
}
